import { variationsGame, viewport } from "@/constants"
import {
  FaChessBishop,
  FaRegChessKing,
  FaChessKing,
  FaRegChessPawn,
  FaChessPawn,
  FaRegChessQueen,
  FaChessKnight,
} from "react-icons/fa6"
import { Cog6ToothIcon } from "@heroicons/react/24/solid"
import { ChevronDownIcon } from "@heroicons/react/20/solid"
import Game from "./Game"
import clsx from "clsx"
import { IconType } from "react-icons"
import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"

const containerVariants = {
  hidden: { opacity: 0, y: -10, scale: 0.95 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  hide: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
}

const movesContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
}

const item = {
  hidden: { opacity: 0 },
  show: { opacity: 1 },
}

const Move = ({
  text,
  icon: Icon,
  highlight = false,
  row,
}: {
  text: string
  icon: IconType
  highlight?: boolean
  row: 2 | 3
}) => (
  <motion.div
    variants={item}
    className={clsx(
      "relative flex h-9 w-20 items-center justify-center space-x-0.5 overflow-hidden rounded-xl border border-gray-200 bg-white text-sm text-gray-900",
      highlight ? "border-gray-400" : "border-gray-200",
      { "row-start-2": row === 2, "row-start-3": row === 3 }
    )}
  >
    {highlight && (
      <div className="border-b-chessdrop-500 absolute -top-3 -left-3 -rotate-45 border-12 border-t-transparent border-r-transparent border-l-transparent" />
    )}
    <Icon className="h-3.5 w-3.5" />
    <span>{text}</span>
  </motion.div>
)

const fullComment = "This wouldn’t have been a great move."

const Variations = () => {
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState("")

  useEffect(() => {
    if (!showComment) return setComment("")
    if (comment === fullComment) return
    ;(async () => {
      await new Promise((r) => {
        setTimeout(r, 25)
      })

      setComment((prev) => prev + fullComment[prev.length])
    })()
  }, [showComment, comment])

  return (
    <div className="h-[239px] w-full max-w-[466px] select-none lg:h-[251px] lg:max-w-none">
      <Game {...variationsGame} />

      <AnimatePresence>
        <motion.div
          initial="hidden"
          whileInView="show"
          exit="hide"
          viewport={viewport}
          variants={containerVariants}
          className="relative mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 px-6 pb-3 shadow-lg"
        >
          <motion.div
            variants={movesContainer}
            className="grid w-[416px] grid-cols-5 grid-rows-3 gap-1 border-b border-gray-200 pb-3"
            onAnimationComplete={() => setShowComment(true)}
            onAnimationStart={() => setShowComment(false)}
          >
            <div className="flex items-end text-xs text-gray-500">1.</div>
            <div className="col-start-3 flex items-end text-xs text-gray-500">
              2.
            </div>
            <div className="col-start-5 flex items-end text-xs text-gray-500">
              3.
            </div>
            <Move text="e7" icon={FaChessBishop} highlight row={2} />
            <Move text="0-0" icon={FaRegChessKing} row={2} />
            <Move text="0-0" icon={FaChessKing} row={2} />
            <Move text="c2" icon={FaRegChessQueen} row={2} />
            <Move text="e4" icon={FaChessKnight} row={2} />

            <motion.div
              variants={item}
              className="relative row-start-3 flex items-center justify-center overflow-hidden"
            >
              <div
                className="absolute -top-4 -right-9 h-full w-full rounded-xl border-2 border-gray-300"
                aria-hidden="true"
              />
            </motion.div>
            <Move text="b4" icon={FaRegChessPawn} row={3} />
            <Move text="dxc4" icon={FaChessPawn} row={3} />
          </motion.div>

          <div className="flex min-h-[34px] items-center justify-between gap-2 pt-3 lg:min-h-[44px]">
            <div className="py-1 text-sm leading-none text-gray-800 lg:line-clamp-1 lg:text-base">
              {comment}
            </div>
            <div className="hidden items-start space-x-1 lg:flex">
              <div className="row-start-2 flex items-center justify-center space-x-0.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm">
                <span>??</span>
                <ChevronDownIcon className="text-chessdrop-500 h-4 w-4" />
              </div>

              <div className="row-start-2 flex items-center justify-center space-x-0.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm">
                <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default Variations
